import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { useForm } from 'react-hook-form';
import { UpsertCatalogProductsBody } from './UpsertCatalogProductsDialogBody';
import { CatalogProductsFormSchema, type CatalogProductsFormData } from '../create-product-dialog-types';
import { useCatalogProductSubmission } from './hooks/useCatalogProductSubmission';
import { useDialog } from '../../../../../../hooks/use-dialog';
import { INTEGRATION_TYPES } from '@vy/integrations-types';
import {
  AnrokProductEntity,
  NetSuiteProductEntity,
  type CatalogProducts,
  type QuickBooksProduct,
  type XeroProduct,
} from '@vy/types';
import type { PaddleProductPayload } from '@vy/api-definition';
import { VyDialog } from '../../../../../../components/new-design/feedback/VyDialog';
import { withSuspenseBoundary } from '../../../../../../components/utility/SuspenseBoundary';
import { fromNullToUndefined, isNotNullOrUndefined, isNullOrUndefined, Optional } from '@vy/utils';
import { findIntegrationByProviderInEntities } from '@vy/services';
import { useQuickBooksProducts, useXeroProducts } from '../../../../../../hooks/requests/integrations';
import { usePaddleProducts } from '../../../../../../hooks/requests/integrations/paddle';
import { buildQuickBooksEntityFromExternalIntegration, buildXeroEntityFromExternalIntegration, buildPaddleEntityFromExternalIntegration } from './hooks/build-erp-entity-from-external-integration';

type UpsertCatalogProductsDialogProps = {
  openCreateProductDialog: boolean;
  onClose: () => void;
  onProductCreated?: (productName: string) => void;
  onBackToMain?: () => void;
  autoOpen?: boolean;
  editProduct?: CatalogProducts;
};

const UpsertCatalogProductsDialog: React.FC<UpsertCatalogProductsDialogProps> = ({
  openCreateProductDialog,
  onClose,
  onProductCreated,
  onBackToMain,
  autoOpen,
  editProduct,
}) => {
  const { isDialogOpen } = useDialog(autoOpen);
  const isEditMode = isNotNullOrUndefined(editProduct);
  const quickBooksProducts = useQuickBooksProducts();
  const xeroProducts = useXeroProducts();
  const paddleProducts = usePaddleProducts();
  const nullishDefaultValues = getDefaultValues(editProduct, quickBooksProducts.data?.products ?? [], xeroProducts.data?.products ?? [], paddleProducts.data?.products ?? []);

  const {
    handleSubmit: formHandleSubmit,
    register,
    control,
    formState: { errors, isValid },
    reset,
  } = useForm<CatalogProductsFormData>({
    resolver: zodResolver(CatalogProductsFormSchema),
    mode: 'onChange',
    defaultValues: fromNullToUndefined(nullishDefaultValues),
  });

  const { submitCatalogProduct } = useCatalogProductSubmission({
    onSuccess: onProductCreated,
    onClose,
    resetCallerState: reset,
    editProduct,
  });

  const handleSubmit = formHandleSubmit(submitCatalogProduct);

  const handleDialogClose = () => {
    reset();
    onClose();
  };

  const actualOpen = openCreateProductDialog || isDialogOpen;

  return (
    <VyDialog
      open={actualOpen}
      onClose={handleDialogClose}
      label={isEditMode ? 'Edit service product' : 'Standard or service product'}
      ctaText={isEditMode ? 'Update product' : 'Create product'}
      ctaDisabled={!actualOpen}
      cancelButtonText="Cancel"
      disableDividers
      onSubmit={handleSubmit}
      maxWidth="md"
      navigateBack={onBackToMain}
      ctaButtonProps={{
        disabled: !isValid,
        sx: {
          fontSize: '16px',
          width: '166px',
          fontWeight: 500,
          lineHeight: '24px',
          color: '#FFF',
          backgroundColor: '#0D0D48',
          '&:hover': {
            backgroundColor: '#0D0D48',
          },
        },
      }}
    >
      <UpsertCatalogProductsBody control={control} register={register} errors={errors} />
    </VyDialog>
  );
};

function getDefaultValues(
  editProduct: Optional<CatalogProducts>,
  quickBooksProducts: QuickBooksProduct[],
  xeroProducts: XeroProduct[],
  paddleProductsList: PaddleProductPayload[],
): Optional<CatalogProductsFormData> {
  if (isNullOrUndefined(editProduct)) {
    return;
  }

  const netSuiteIntegrationEntity = findIntegrationByProviderInEntities<NetSuiteProductEntity>(
    editProduct.integrationEntities ?? [],
    INTEGRATION_TYPES.NETSUITE,
  );

  const quickBooksIntegrationEntity = buildQuickBooksEntityFromExternalIntegration(editProduct.externalIntegration, quickBooksProducts);
  const xeroIntegrationEntity = buildXeroEntityFromExternalIntegration(editProduct.externalIntegration, xeroProducts);
  const paddleIntegrationEntity = buildPaddleEntityFromExternalIntegration(editProduct.externalIntegration, paddleProductsList);

  const anrokIntegrationEntity = findIntegrationByProviderInEntities<AnrokProductEntity>(
    editProduct.integrationEntities ?? [],
    INTEGRATION_TYPES.ANROK,
  );

  return {
    name: editProduct.name,
    description: editProduct.description ?? '',
    isTaxable: editProduct.isTaxable ?? true,
    creditTypeIds: editProduct.creditTypeIds ?? [],
    netSuiteProduct: isNotNullOrUndefined(netSuiteIntegrationEntity) ? [netSuiteIntegrationEntity] : [],
    netSuiteClass: netSuiteIntegrationEntity?.metadata?.classMetadata ?? undefined,
    quickBooksProduct: isNotNullOrUndefined(quickBooksIntegrationEntity) ? [quickBooksIntegrationEntity] : [],
    xeroProduct: isNotNullOrUndefined(xeroIntegrationEntity) ? [xeroIntegrationEntity] : [],
    paddleProduct: isNotNullOrUndefined(paddleIntegrationEntity) ? [paddleIntegrationEntity] : [],
    anrokProduct: isNotNullOrUndefined(anrokIntegrationEntity) ? [anrokIntegrationEntity] : [],
  };
}

const UpsertCatalogProductsDialogWithSuspense = withSuspenseBoundary(UpsertCatalogProductsDialog);

export { UpsertCatalogProductsDialogWithSuspense as UpsertCatalogProductsDialog };
